import { LS_JWT_KEY, LS_LANG_KEY, REQUEST_TIMEOUT, GENDER_VALUES, ERROR_SESSION_EXPIRED, ADMIN_ROUTES, LANG_CS } from "~/const";
import { trans, transError } from "~/utils/trans";
import { userStore } from "~/stores/user";
import { langStore } from "~/stores/lang";
import { IMySelectItem, IFetchOutput, IJsonData, RequestInit, IRecipePart } from "~/interfaces";
import { ICalendarDayData } from "~/components/Calendar";
import { IListMenuOutput, IUser } from "~/providers/interfaces";

export function getJWT(): string {
	return localStorage.getItem(LS_JWT_KEY) || "";
}

export function setJWT(jwt: string): void {
	if (jwt) {
		localStorage.setItem(LS_JWT_KEY, jwt || "");
	} else {
		localStorage.removeItem(LS_JWT_KEY);
	}
}

export function getLang(): string {
	return localStorage.getItem(LS_LANG_KEY) || "";
}

export function setLang(lang: string): void {
	if (lang) {
		localStorage.setItem(LS_LANG_KEY, lang || "");
	} else {
		localStorage.removeItem(LS_LANG_KEY);
	}
}

export const abortFetchSignal = (timeoutMs: number) => {
	if (typeof window === 'undefined' || window.AbortController === undefined) {
		return undefined;
	}

	const abortController = new AbortController();

	setTimeout(() => abortController.abort(), timeoutMs);

	return abortController.signal;
};

// jazyk textu z DB pro pozadavek, administrace vzdy cesky
export function getRequestLang(): string {
	return ADMIN_ROUTES.includes(window.location.pathname) ? LANG_CS : langStore.getState().lang;
}

export async function fetchJSON<T>(path: string, options?: RequestInit, defaultData?: any, changeData?: (data: any) => any): Promise<IFetchOutput<T>> {
	let request = null;

	try {
		/* eslint-disable no-magic-numbers */
		request = await fetch(path, {
			headers: {
				"Accept": 'application/json',
				// server podle nej vraci texty z DB (recepty, suroviny…)
				"X-Lang": getRequestLang(),
				...options && options.method !== "GET"
					? {
						"Content-Type": "application/json",
					}
					: {},
			},
			credentials: 'include',
			signal: abortFetchSignal(REQUEST_TIMEOUT),
			...options,
		});
		const jsonData: IJsonData<T> = await request.json();
		const isError = Boolean(jsonData.error) || request.status < 200 || request.status > 299;
		let data = defaultData && jsonData.data === null ? defaultData : jsonData.data;

		if (typeof changeData === "function") {
			data = changeData(data);
		}

		if (isError) {
			// technicke detaily jen do konzole, uzivatel dostane prelozenou hlasku
			/* eslint-disable no-console */
			console.error(`Lokace [${options.method || "GET"}] ${path} status ${request.status}`, jsonData.error, jsonData.errorParams);

			// neplatne prihlaseni - odhlasime, stranka sama nabidne prihlaseni
			if (jsonData.error === ERROR_SESSION_EXPIRED) {
				setJWT("");
				userStore.getState().logout();
			}
		}

		return {
			data,
			status: request.status,
			noErrors: !isError,
			errors: isError
				? [transError(jsonData.error || "common.unknown", jsonData.errorParams)]
				: [],
			jwt: jsonData.jwt || "",
			logged: jsonData.logged || false,
		};
	} catch (exc) {
		/* eslint-disable no-console */
		console.error(`Lokace [${options.method || "GET"}] ${path} obsahuje chybu`, exc);

		// fetch selze (TypeError) nebo je zrusen po casovem limitu (AbortError) - nedostupny server nebo sit,
		// jinak server vratil neco jineho nez JSON
		const isNetwork = exc instanceof TypeError || (exc as Error)?.name === "AbortError";

		return {
			data: null,
			status: request?.status || 500,
			noErrors: false,
			errors: [transError(isNetwork ? "common.network" : "common.unknown")],
			jwt: "",
			logged: false,
		};
	}
}

export function loadScript(src: string) {
	return new Promise((resolve, reject) => {
		const script = document.createElement("script");

		script.type = "text/javascript";
		script.async = true;
		script.addEventListener("load", () => {
			resolve({});
		});
		script.addEventListener("error", () => {
			reject({});
		});

		script.src = src;
		document.head.appendChild(script);
	});
}

/* eslint-disable no-magic-numbers */
export function timeToSeconds(value: string): number {
	const parts = value.split(":");

	if (parts.length === 2) {
		return (parseFloat(parts[0]) * 60) + parseFloat(parts[1]);
	}

	return 0;
}

/**
 * Combines strings and/or objects into one single string usable as a value for the className property
 */
export const classNames = (classes: Array<string | { [key: string]: boolean }> = []): string => (Array.isArray(classes)
	? classes
	: [classes]).reduce((allClasses, className) => {
	// an empty string will still pass - it is faster and breaks nothing
	if (typeof className === 'string') {
		allClasses.push(className);
	} else {
		// every truthy property is included in the final className
		className && Object.keys(className).forEach(key => className[key] && allClasses.push(key));
	}

	return allClasses.filter(item => item);
}, []).join(' ');

export function isLocalDev(): boolean {
	return location.origin.indexOf("127.0.0.1") !== -1 || location.origin.indexOf("localhost") !== -1;
}

export function getGenderItems(): Array<IMySelectItem> {
	return GENDER_VALUES.map(item => ({
		...item,
		title: trans(item.transTitle),
	}));
}

export function getFoodsItems(): Array<IMySelectItem> {
	return Array.from({ length: 4 }).map((item, ind) => ({
		value: (ind+3).toString(),
		title: trans("profilePage.mealsPerDay", { count: ind + 3 }),
	}));
}

export function checkEmail(email: string): boolean {
	return email.match(/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/iu) !== null;
}

export function updateItemInArray<T>(inputArray: Array<T>, compareCb: (item: T) => boolean, updateCb: (item: T) => T): Array<T> {
	const filtered = inputArray.filter(item => compareCb(item));

	if (filtered.length === 1) {
		Object.assign(filtered[0], updateCb(filtered[0]));
	}

	return inputArray;
}

export function getIndexFromArray<T>(inputArray: Array<T>, compareCb: (item: T) => boolean): number {
	for (let ind = 0, max = inputArray.length; ind < max; ind++) {
		if (compareCb(inputArray[ind])) {
			return ind;
		}
	}

	return -1;
}

export function removeFromArray<T>(inputArray: Array<T>, compareCb: (item: T) => boolean): Array<T> {
	const ind = getIndexFromArray(inputArray, compareCb);

	if (ind !== -1) {
		const copiedArray = inputArray.slice();

		copiedArray.splice(ind, 1);

		return copiedArray;
	}

	return inputArray;
}

export function compareArray<T>(arrayA: Array<T>, arrayB: Array<T>): boolean {
	const len = arrayA.length;

	if (len !== arrayB.length) {
		return false;
	} else if (!len) {
		return true;
	}

	let areSame = false;

	arrayA.every((itemA, ind) => {
		const itemB = arrayB[ind];

		areSame = itemA === itemB;

		return areSame;
	});

	return areSame;
}

export function calToKj(calValue: number): number {
	return calValue * 4.184;
}

export function getRandomValue(maxValue = 1000) {
	return Math.random() * maxValue >>> 0;
}

export function fileToImg(file: File): Promise<HTMLImageElement> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();

		reader.addEventListener("load", () => {
			const image = new Image();

			image.src = reader.result.toString();
			resolve(image);
		});
		reader.addEventListener("error", error => {
			reject(error);
		});
		reader.readAsDataURL(file);
	});
}

export function alignImage(image: HTMLImageElement, size: number): string {
	const canvas = document.createElement("canvas");
	const ctx = canvas.getContext("2d");

	canvas.width = size;
	canvas.height = size;

	const width = image.naturalWidth;
	const height = image.naturalHeight;
	let offsetX = 0;
	let offsetY = 0;
	let imgWidth = 0;
	let imgHeight = 0;

	if (width > height) {
		imgHeight = size;
		imgWidth = Math.floor(width / height * size);
		offsetX = Math.floor((imgWidth - size) / 2);
	} else {
		imgWidth = size;
		imgHeight = Math.floor(height / width * size);
		offsetY = Math.floor((imgHeight - size) / 2);
	}

	ctx.drawImage(image, offsetX, offsetY, width, height, 0, 0, imgWidth, imgHeight);

	return canvas.toDataURL();
}

// vraci klice chybejicich poli (notifications.profile.fields.<klic>), prekladaji se az pri zobrazeni
export function getProfileErrors(userData: IUser, weight: number): Array<string> {
	const errors = [];

	if (userData.birthdate === "") {
		errors.push("birthdate");
	}

	if (weight <= 0) {
		errors.push("weight");
	}

	if (userData.height <= 0) {
		errors.push("height");
	}

	if (userData.sex === "") {
		errors.push("sex");
	}

	return errors;
}

export function parseRecipeParts(recipe: string) {
	const parts: Array<IRecipePart> = [];
	let value = "";
	let expr = "";

	for (let ind = 0, max = recipe.length; ind < max; ind++) {
		const sign = recipe[ind];

		if (sign === "#") {
			if (expr) {
				parts.push({
					type: "expr",
					value: `${expr}#`,
				});
				expr = "";
			} else {
				parts.push({
					type: "text",
					value,
				});
				value = "";
				expr = sign;
			}
		} else if (expr) {
			expr += sign;
		} else {
			value += sign;
		}
	}

	if (value) {
		parts.push({
			type: "text",
			value,
		});
	} else if (expr) {
		parts.push({
			type: "expr",
			value: `${expr}#`,
		});
	}

	return parts;
}

// photoSrc - images/...
export function getImagePath(photoSrc: string): string {
	return `/server/${photoSrc}`;
}

// dny kalendare z jidelnicku - isPaid (zlute zvyrazneni) = den s jidelnickem, placeni za dny uz neexistuje
export function mergeCalendarItems(listMenu: Array<IListMenuOutput>) {
	const items: Array<ICalendarDayData> = [];

	listMenu.forEach(listMenuItem => {
		const filtered = items.filter(item => item.beDate === listMenuItem.date);
		let item: ICalendarDayData = null;

		// update
		if (filtered.length) {
			item = filtered[0];
		} else {
			item = {
				beDate: listMenuItem.date,
				// automaticky je placene
				isPaid: true,
			};
			items.push(item);
		}

		item.hasMeals = listMenuItem.menu.length > 0;
		item.menuData = listMenuItem;
	});

	return items;
}

export function roundNumberTo(value: number, dec: number) {
	const strValue = value.toFixed(dec);

	return strValue.replace(/([0-9]+(\.[0-9]+[1-9])?)(\.?0+$)/u, "$1");
}
